import { ErrorMessage } from '@hookform/error-message';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useMediaQuery } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { styled as muiStyled, css, useTheme } from '@mui/material/styles';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import SmallForm from './small-form';
import LogoSelector from '../base/icon/logo-selector';
import { useSnackbarContext } from '../base/snackbar';

import Arranger from '~c/components/base/arranger';
import Button from '~c/components/base/button';
import Link from '~c/components/base/link';
import { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';
import TextField from '~c/components/base/text-field';
import {
  useYupValidationResolver,
  muiError,
} from '~c/hooks/yup-validation-resolver';
import { nodeAuthApi } from '~c/services';

interface InputData {
  newPassword: string;
  repeatPassword: string;
  loginResult: string;
}

const validationSchema = yup.object().shape({
  newPassword: yup
    .string()
    .trim()
    .validatePasswordStrength()
    .required('New password is required.'),
  repeatPassword: yup
    .string()
    .trim()
    .validatePasswordStrength()
    .required('Confirm password is required')
    .oneOf([yup.ref('newPassword')], 'Passwords do not match'),
});

interface Props {
  className?: string;
}

const CreatePasswordImpl: React.FC<Props> = ({ className }) => {
  const navigate = useNavigate();
  const { code } = useParams();
  const theme = useTheme();
  const prefersDarkMode = theme.palette.mode === 'dark';

  const [sendCreatePassword, { isLoading }] =
    nodeAuthApi.useCreatePasswordMutation();

  const resolver = useYupValidationResolver(validationSchema);
  const { register, formState, setError, handleSubmit } = useForm<InputData>({
    mode: 'onChange',
    resolver,
  });
  const [showPasswordNew, setShowPasswordNew] = React.useState(false);
  const [showPasswordRepeat, setShowPasswordRepeat] = React.useState(false);
  const Snackbar = useSnackbarContext();
  const { errors } = formState;

  const toggleShowPasswordNew = () => setShowPasswordNew((show) => !show);
  const toggleShowPasswordRepeat = () => setShowPasswordRepeat((show) => !show);

  const onSubmit = async (input: InputData) => {
    await sendCreatePassword({
      resetPasswordId: code ?? '',
      newPassword: input.newPassword,
    })
      .then((resp: any) => {
        if ('data' in resp && !('error' in resp?.data)) {
          Snackbar.open(`${resp.data.message}`, {
            showOk: true,
          });
          navigate('/sign-in');
        } else {
          setError('loginResult', { message: `${resp.error.data.error}` });
        }
      })
      .catch(() => {
        // handle error
      });
  };

  return (
    <SmallForm
      className={className}
      title={
        <Arranger direction="column" alignItems="center">
          <div className="logo-container">
            <LogoSelector prefersDarkMode={prefersDarkMode} />
          </div>
          <Text sz="h1" sx={{ pt: spc.xxxs }}>
            Create your password
          </Text>
        </Arranger>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          {...register('newPassword')}
          {...muiError(errors, 'newPassword')}
          required
          fullWidth
          label="New password"
          type={showPasswordNew ? 'text' : 'password'}
          autoComplete="off"
          sx={{ pb: spc.between_vertical_s }}
          startAdornment={<LockIcon />}
          endAdornment={
            <IconButton onClick={toggleShowPasswordNew}>
              {showPasswordNew ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          }
        />
        <TextField
          {...register('repeatPassword')}
          {...muiError(errors, 'repeatPassword')}
          required
          fullWidth
          label="Confirm New Password"
          type={showPasswordRepeat ? 'text' : 'password'}
          autoComplete="off"
          sx={{ pb: spc.between_vertical_s }}
          startAdornment={<LockIcon />}
          endAdornment={
            <IconButton onClick={toggleShowPasswordRepeat}>
              {showPasswordRepeat ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          }
        />
        <input {...register('loginResult')} hidden />
        <ErrorMessage
          as={<Text color="error" />}
          errors={errors}
          name="loginResult"
        />
        <Arranger direction="column" alignItems="center">
          <Button
            loading={isLoading}
            type="submit"
            // size="large"
            sx={{ mt: spc.xs, mb: spc.between_vertical_s }}
          >
            Create password
          </Button>
        </Arranger>
        <Arranger justify="space-between">
          <Link href="/sign-in" variant="body2">
            Sign In
          </Link>
        </Arranger>
      </form>
    </SmallForm>
  );
};

export const CreatePassword = muiStyled(CreatePasswordImpl)(
  ({ theme }) => css`
    .logo-container {
      svg {
        width: 150px;
        height: 50px;
      }
    }
  `
);

export default CreatePassword;
